import React, { useEffect, useState, useCallback, useRef } from "react";
import { Box, Button, Typography, CircularProgress } from "@mui/material";
import {
  type FocusSessionVariant,
  type MmkFilterCategory,
  type MmkFocusSessionCategory,
  type MmkSortOrder,
  type MmkViewMode,
} from "../../types/mmk";
import { FocusSessionEmpty } from "./focus-session-empty";
import { FocusSessionView } from "./focus-session-view";
import { QuizLoading } from "./quiz-loading";
import { QuizEmpty } from "./quiz-empty";
import { QuizView } from "./quiz-view";
import { QuizSummary } from "./quiz-summary";
import { MmkMainContent } from "./mmk-main-content";
import {
  useMmkItems,
  useMmkQuiz,
  useMmkFocusSession,
  getFocusSessionItems,
} from "../../hooks";
import { useMmkStore } from "../../stores/mmkStore";
import {
  postMmkItemAction,
  getQuizItems,
  postQuizComplete,
} from "../../axios/mmk";
import {
  loadQuizState,
  loadFocusSessionState,
  loadSortOrder,
  saveSortOrder,
  sortMmkItems,
  loadMmkUiState,
  saveMmkUiState,
} from "../../utils/mmkPersistence";
import { toast } from "react-toastify";
import { trackMmkEvent } from "../../utils/mmkAnalytics";
import {
  getMmkCardDisplayLabel,
  toMmkPublicLabel,
} from "../../helpers/mmkDisplayLabel";
import { isPersonalMmkItem } from "../../helpers/mmkPersonal";
import { mmkBackToMmkButtonStyles } from "./styles";
import { MmkPageShell } from "./mmk-page-shell";

interface MmkSectionProps {
  onCardInteract?: () => void;
}

export const MmkSection: React.FC<MmkSectionProps> = ({ onCardInteract }) => {
  const {
    items,
    totalCount,
    loading,
    error,
    scopeKey,
    toggleMastered,
    markAsMastered,
    togglePin,
    toggleReviewLater,
    markQuizWrong,
  } = useMmkItems();
  const { setQuizInProgress, setFocusSessionInProgress } = useMmkStore();

  const [activeCategory, setActiveCategory] =
    useState<MmkFilterCategory>("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState<MmkViewMode>("scroll");
  const [variantModalOpen, setVariantModalOpen] = useState(false);
  const [focusSessionVariant, setFocusSessionVariant] =
    useState<FocusSessionVariant>("mixed");
  const [focusSessionCategory, setFocusSessionCategory] = useState<
    MmkFocusSessionCategory | ""
  >("");
  const [activeCardId, setActiveCardId] = useState<string | null>(null);
  const [flippedCardId, setFlippedCardId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [sortOrder, setSortOrder] = useState<MmkSortOrder>("oldest_first");

  const quiz = useMmkQuiz(scopeKey, markQuizWrong, markAsMastered);
  const focusSession = useMmkFocusSession(
    scopeKey,
    items,
    toggleMastered,
  );
  const { startFocusSessionWithItems } = focusSession;

  // Restore MMM filters/view per focus (scopeKey), with safe defaults
  useEffect(() => {
    if (!scopeKey) return;
    const ui = loadMmkUiState(scopeKey);
    if (ui) {
      setActiveCategory(ui.activeCategory);
      setSearchTerm(ui.searchTerm);
      setDebouncedSearchTerm(ui.searchTerm);
      setViewMode(ui.viewMode);
      setIsSelectionMode(ui.isSelectionMode);
      setSelectedIds(new Set());
      setActiveCardId(null);
      setFlippedCardId(null);
    } else {
      setActiveCategory("All");
      setSearchTerm("");
      setDebouncedSearchTerm("");
      setViewMode("scroll");
      setIsSelectionMode(false);
      setSelectedIds(new Set());
      setActiveCardId(null);
      setFlippedCardId(null);
    }
  }, [scopeKey]);

  // Persist MMM filters/view per focus (scopeKey)
  useEffect(() => {
    if (!scopeKey) return;
    saveMmkUiState(scopeKey, {
      activeCategory,
      searchTerm,
      viewMode,
      isSelectionMode,
    });
  }, [scopeKey, activeCategory, searchTerm, viewMode, isSelectionMode]);

  useEffect(() => {
    if (variantModalOpen) setFocusSessionCategory("");
  }, [variantModalOpen]);

  useEffect(() => {
    setQuizInProgress(quiz.isQuiz);
    return () => setQuizInProgress(false);
  }, [quiz.isQuiz, setQuizInProgress]);

  useEffect(() => {
    setFocusSessionInProgress(focusSession.isFocusSession);
    return () => setFocusSessionInProgress(false);
  }, [focusSession.isFocusSession, setFocusSessionInProgress]);

  const hasPendingFocusSessionRestore =
    !loading &&
    !!scopeKey &&
    !focusSession.hasRestoreBeenAttempted &&
    !loadQuizState(scopeKey) &&
    !!loadFocusSessionState(scopeKey)?.sessionItemIds?.length;

  const { setPrefetchedQuizQuestions } = quiz;
  useEffect(() => {
    if (items.length > 0 && totalCount >= 1) {
      getQuizItems(5)
        .then((res) => setPrefetchedQuizQuestions(res.questions))
        .catch(() => {});
    }
  }, [items.length, totalCount, setPrefetchedQuizQuestions]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearchTerm(searchTerm), 250);
    return () => window.clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    if (scopeKey) {
      const saved = loadSortOrder(scopeKey);
      if (saved) setSortOrder(saved);
    }
  }, [scopeKey]);

  useEffect(() => {
    trackMmkEvent("Open MMM");
  }, []);

  const quizCompleteSentRef = useRef(false);
  useEffect(() => {
    const isComplete =
      quiz.isQuiz &&
      quiz.quizQuestions.length > 0 &&
      quiz.quizIndex >= quiz.quizQuestions.length;
    if (!isComplete) {
      quizCompleteSentRef.current = false;
      return;
    }
    if (quizCompleteSentRef.current) return;
    quizCompleteSentRef.current = true;
    const categoryBreakdown = quiz.buildCategoryBreakdown(
      quiz.quizQuestions,
      quiz.quizCorrectIds,
      quiz.quizWrongIds,
    );
    postQuizComplete({
      score: quiz.quizCorrectIds.length,
      total: quiz.quizQuestions.length,
      correct_ids: quiz.quizCorrectIds,
      wrong_ids: quiz.quizWrongIds,
      category_breakdown: categoryBreakdown,
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only send once on quiz complete; ref prevents duplicates
  }, [
    quiz.isQuiz,
    quiz.quizQuestions.length,
    quiz.quizIndex,
    quiz.quizCorrectIds,
    quiz.quizWrongIds,
  ]);

  const handleSortOrderChange = useCallback(
    (order: MmkSortOrder) => {
      setSortOrder(order);
      if (scopeKey) saveSortOrder(scopeKey, order);
      trackMmkEvent("Toggle sort", { sort: order });
    },
    [scopeKey],
  );

  const masteredCount = items.filter((i) => i.is_mastered).length;
  const totalToReviewCount = items.filter((i) => !i.is_mastered).length;
  const todayReviewCount = Math.min(5, totalToReviewCount);
  const allKnown = totalCount > 0 && masteredCount === totalCount;

  const matchesSearch = (i: (typeof items)[number]) => {
    const q = (i.question || i.title || "").toLowerCase();
    const a = (i.approved_answer || i.content || "").toLowerCase();
    const short = (i.short_answer || "").toLowerCase();
    const s = debouncedSearchTerm.trim().toLowerCase();
    return !s || q.includes(s) || a.includes(s) || short.includes(s);
  };

  const searchMatchedItems = items.filter(matchesSearch);

  const categoryCounts: Record<MmkFilterCategory, number> = {
    All: searchMatchedItems.length,
    Personal: searchMatchedItems.filter((i) => i.source === "personal").length,
    Indication: searchMatchedItems.filter((i) => i.category === "Indication")
      .length,
    Dosing: searchMatchedItems.filter((i) => i.category === "Dosing").length,
    Safety: searchMatchedItems.filter((i) => i.category === "Safety").length,
    Efficacy: searchMatchedItems.filter((i) => i.category === "Efficacy")
      .length,
    "Patient Selection": searchMatchedItems.filter(
      (i) => i.category === "Patient Selection",
    ).length,
    Differentiation: searchMatchedItems.filter(
      (i) => i.category === "Differentiation",
    ).length,
  };

  const filteredItems =
    activeCategory === "All"
      ? searchMatchedItems
      : activeCategory === "Personal"
        ? searchMatchedItems.filter((i) => i.source === "personal")
        : searchMatchedItems.filter((i) => i.category === activeCategory);

  const searchFilteredItems = filteredItems;

  const pinnedItems = sortMmkItems(
    searchFilteredItems.filter((i) => i.is_pinned),
    sortOrder,
  );
  const toReviewItems = sortMmkItems(
    searchFilteredItems.filter((i) => !i.is_pinned && !i.is_mastered),
    sortOrder,
  );
  const knownItems = sortMmkItems(
    searchFilteredItems.filter((i) => !i.is_pinned && i.is_mastered),
    sortOrder,
  );

  const releaseFocusAndClose = useCallback(() => {
    setTimeout(() => {
      setActiveCardId(null);
      setFlippedCardId(null);
      (document.activeElement as HTMLElement)?.blur();
    }, 120);
  }, []);

  const handleCardClick = useCallback(
    (id: string) => {
      if (flippedCardId === id) {
        setFlippedCardId(null);
        return;
      }
      onCardInteract?.();
      setActiveCardId(id);
      setFlippedCardId(id);
      trackMmkEvent("Flip card", { item_id: id, view_mode: viewMode });
      postMmkItemAction(id, "flip").catch(() => {});
    },
    [flippedCardId, viewMode, onCardInteract],
  );

  const handleCloseFlip = useCallback(() => {
    setActiveCardId(null);
    setFlippedCardId(null);
  }, []);

  const handlePin = useCallback(
    async (id: string) => {
      trackMmkEvent("Click PIN", { item_id: id });
      await togglePin(id);
      releaseFocusAndClose();
    },
    [togglePin, releaseFocusAndClose],
  );

  const handleReviewLater = useCallback(
    async (id: string) => {
      trackMmkEvent("Click Review Later", { item_id: id });
      await toggleReviewLater(id);
      releaseFocusAndClose();
    },
    [toggleReviewLater, releaseFocusAndClose],
  );

  const handleKnowIt = useCallback(
    async (id: string) => {
      trackMmkEvent("Click I Know It", { item_id: id });
      await toggleMastered(id);
      setFlippedCardId(null);
      releaseFocusAndClose();
    },
    [toggleMastered, releaseFocusAndClose],
  );

  const handleStartFocusSession = useCallback(() => {
    const focusItems =
      focusSessionVariant === "by_category"
        ? getFocusSessionItems(
            items,
            focusSessionVariant,
            focusSessionCategory,
          )
        : getFocusSessionItems(items, focusSessionVariant);
    trackMmkEvent("Start Focus Session", { cards: focusItems.length });
    startFocusSessionWithItems(focusItems);
    setVariantModalOpen(false);
  }, [
    items,
    focusSessionVariant,
    focusSessionCategory,
    startFocusSessionWithItems,
  ]);

  const handleToggleSelectionMode = useCallback(() => {
    setIsSelectionMode((prev) => {
      if (prev) {
        setSelectedIds(new Set());
        setActiveCardId(null);
        setFlippedCardId(null);
      } else {
        setActiveCardId(null);
        setFlippedCardId(null);
      }
      return !prev;
    });
  }, []);

  const handleBatchHide = useCallback(async () => {
    await Promise.all([...selectedIds].map((id) => toggleReviewLater(id)));
    setSelectedIds(new Set());
    trackMmkEvent("Batch Hide", { count: selectedIds.size });
  }, [selectedIds, toggleReviewLater]);

  const handleBatchPractice = useCallback(() => {
    const selectedItems = items.filter((i) => selectedIds.has(i.id));
    if (selectedItems.length === 0) return;
    startFocusSessionWithItems(selectedItems);
    setSelectedIds(new Set());
    trackMmkEvent("Batch Practice", { count: selectedItems.length });
  }, [items, selectedIds, startFocusSessionWithItems]);

  if (loading || hasPendingFocusSessionRestore) {
    return (
      <MmkPageShell>
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      </MmkPageShell>
    );
  }

  if (error) {
    return (
      <MmkPageShell>
        <Typography color="error" sx={{ py: 4, textAlign: "center" }}>
          {error}
        </Typography>
      </MmkPageShell>
    );
  }

  if (focusSession.isFocusSession && focusSession.sessionItems.length === 0) {
    return (
      <FocusSessionEmpty onBack={focusSession.handleExitFocusSession} />
    );
  }

  if (focusSession.isFocusSession && focusSession.sessionItems.length > 0) {
    const sessionItem =
      focusSession.sessionItems[focusSession.focusSessionIndex];
    const currentItem = sessionItem
      ? (items.find((i) => i.id === sessionItem.id) ?? sessionItem)
      : null;

    if (!currentItem) {
      return (
        <MmkPageShell>
          <Button
            variant="outlined"
            onClick={focusSession.handleExitFocusSession}
            sx={mmkBackToMmkButtonStyles}
          >
            Back to MMM
          </Button>
        </MmkPageShell>
      );
    }

    return (
      <FocusSessionView
        currentItem={currentItem}
        sessionItems={focusSession.sessionItems}
        sessionTotal={focusSession.sessionTotal}
        focusSessionIndex={focusSession.focusSessionIndex}
        flipped={focusSession.focusSessionFlipped}
        onFlip={focusSession.setFocusSessionFlipped}
        onPrev={focusSession.handleFocusSessionPrev}
        onNext={focusSession.handleFocusSessionNext}
        onMarkAsKnown={focusSession.handleMarkAsKnown}
        onExit={focusSession.handleExitFocusSession}
        onPin={handlePin}
        onReviewLater={handleReviewLater}
      />
    );
  }

  if (quiz.isQuiz && quiz.quizLoading) {
    return <QuizLoading onBack={quiz.handleExitQuiz} />;
  }

  if (quiz.isQuiz && !quiz.quizLoading && quiz.quizQuestions.length === 0) {
    return <QuizEmpty onBack={quiz.handleExitQuiz} />;
  }

  if (
    quiz.isQuiz &&
    quiz.quizQuestions.length > 0 &&
    quiz.quizIndex >= quiz.quizQuestions.length
  ) {
    const categoryBreakdown = quiz.buildCategoryBreakdown(
      quiz.quizQuestions,
      quiz.quizCorrectIds,
      quiz.quizWrongIds,
    );
    return (
      <QuizSummary
        score={quiz.quizCorrectIds.length}
        total={quiz.quizQuestions.length}
        categoryBreakdown={categoryBreakdown}
        onBack={quiz.handleExitQuiz}
      />
    );
  }

  if (
    quiz.isQuiz &&
    quiz.quizQuestions.length > 0 &&
    quiz.quizIndex < quiz.quizQuestions.length
  ) {
    const currentQuestion = quiz.quizQuestions[quiz.quizIndex];
    const quizItem = items.find((i) => i.id === currentQuestion.item_id);
    const card_header_label = quizItem
      ? getMmkCardDisplayLabel(quizItem)
      : toMmkPublicLabel(currentQuestion.mkk_number ?? "");
    const questionWithMkk = {
      ...currentQuestion,
      ...(quizItem &&
      !isPersonalMmkItem(quizItem) &&
      quizItem.mkk_number
        ? { mkk_number: quizItem.mkk_number }
        : {}),
      card_header_label,
    };
    const answerKey = `${currentQuestion.item_id}::${quiz.quizIndex}`;
    const answeredDetail = quiz.quizAnsweredDetails[answerKey];
    return (
      <QuizView
        key={currentQuestion.item_id}
        question={questionWithMkk}
        questionIndex={quiz.quizIndex}
        totalQuestions={quiz.quizQuestions.length}
        onAnswer={quiz.handleQuizAnswer}
        onNext={quiz.handleQuizNext}
        onExit={quiz.handleExitQuiz}
        initialRevealed={!!answeredDetail}
        initialSelectedOptionIndex={answeredDetail?.selectedOptionIndex}
      />
    );
  }

  return (
    <MmkMainContent
      items={items}
      totalCount={totalCount}
      masteredCount={masteredCount}
      totalToReviewCount={totalToReviewCount}
      todayReviewCount={todayReviewCount}
      pinnedItems={pinnedItems}
      toReviewItems={toReviewItems}
      knownItems={knownItems}
      allKnown={allKnown}
      activeCategory={activeCategory}
      categoryCounts={categoryCounts}
      searchTerm={searchTerm}
      viewMode={viewMode}
      sortOrder={sortOrder}
      onSortOrderChange={handleSortOrderChange}
      isSelectionMode={isSelectionMode}
      selectedIds={selectedIds}
      activeCardId={activeCardId}
      flippedCardId={flippedCardId}
      variantModalOpen={variantModalOpen}
      focusSessionVariant={focusSessionVariant}
      focusSessionCategory={focusSessionCategory}
      onCategoryChange={setActiveCategory}
      onSearchChange={(v) => setSearchTerm(v)}
      onViewModeChange={setViewMode}
      onToggleSelectionMode={handleToggleSelectionMode}
      onClearSelection={() => setSelectedIds(new Set())}
      onBatchHide={handleBatchHide}
      onBatchPractice={handleBatchPractice}
      onCardFlip={handleCardClick}
      onCloseFlip={handleCloseFlip}
      onPin={handlePin}
      onReviewLater={handleReviewLater}
      onKnowIt={handleKnowIt}
      onToggleSelect={(id) => {
        setSelectedIds((prev) => {
          const next = new Set(prev);
          if (next.has(id)) next.delete(id);
          else next.add(id);
          return next;
        });
      }}
      onVariantModalOpen={() => setVariantModalOpen(true)}
      onVariantModalClose={() => setVariantModalOpen(false)}
      onVariantSelect={setFocusSessionVariant}
      onCategorySelect={setFocusSessionCategory}
      onStartFocusSession={handleStartFocusSession}
      onStartQuiz={quiz.handleStartQuiz}
      onAllPassedClick={() =>
        toast.info("All questions have been passed!")
      }
      onPersonalCardIdReplaced={(oldId, newId) => {
        setActiveCardId((prev) => (prev === oldId ? newId : prev));
        setFlippedCardId((prev) => (prev === oldId ? newId : prev));
        setSelectedIds((prev) => {
          if (!prev.has(oldId)) return prev;
          const next = new Set(prev);
          next.delete(oldId);
          next.add(newId);
          return next;
        });
      }}
      onPersonalCardDeleted={(id) => {
        setActiveCardId((prev) => (prev === id ? null : prev));
        setFlippedCardId((prev) => (prev === id ? null : prev));
        setSelectedIds((prev) => {
          if (!prev.has(id)) return prev;
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }}
    />
  );
};
